import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Award, CheckCircle, Clock, ShieldCheck, FileText, MessageSquare, ArrowRight } from 'lucide-react';
const proofPoints = [{
  icon: Award,
  title: 'Constructionline Gold & accreditations',
  description: 'One of fewer than 100 HVAC firms in the UK to hold Constructionline Gold. Plus REFCOM Elite, SafeContractor, and CHAS accredited.'
}, {
  icon: CheckCircle,
  title: 'Quality installation',
  description: 'Proper load calculations, correct sizing, full commissioning. Our installations consistently achieve 16-year lifespans — double the industry average.'
}, {
  icon: Clock,
  title: 'In-house aftersales',
  description: 'No 4–6 week waits for manufacturer callouts. Our engineers diagnose and resolve directly, with manufacturer-level capability.'
}, {
  icon: ShieldCheck,
  title: 'Compliance expertise',
  description: 'TM44, F-Gas, ISO 14001. We keep your building legal and your records audit-ready.'
}, {
  icon: FileText,
  title: 'Detailed reporting',
  description: 'Service reports written for humans, not filing cabinets. You will know exactly what was found, what was done, and what to plan for.'
}, {
  icon: MessageSquare,
  title: 'Customer education',
  description: 'We explain what we recommend and why. No upselling, no jargon, no pressure.'
}];
export function WhyAccuTemp() {
  return <main className="min-h-screen font-sans text-primary-dark pt-28 bg-[#E8F0F8]" data-id="element-2576">
      {/* Hero Section */}
      <section className="container mx-auto px-6 pb-20 pt-8" data-id="element-2577">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-start" data-id="element-2578">
          <div className="lg:col-span-7" data-id="element-2579">
            <span className="text-sm uppercase tracking-wider font-medium text-primary-dark/40 mb-6 block" data-id="element-2580">
              Why AccuTemp
            </span>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="text-5xl md:text-6xl lg:text-7xl font-serif text-primary-dark leading-[1.1] tracking-tight" data-id="element-2581">
              Why businesses choose AccuTemp
            </motion.h1>
          </div>
          <div className="lg:col-span-5 lg:pt-14" data-id="element-2582">
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="text-lg text-primary-dark/70 leading-relaxed" data-id="element-2583">
              We do not cut corners. Here is what that actually means for your
              building, your budget, and your compliance.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Proof Points Grid */}
      <section className="bg-white py-24" data-id="element-2584">
        <div className="container mx-auto px-6" data-id="element-2585">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12" data-id="element-2586">
            {proofPoints.map((point, index) => {
            const Icon = point.icon;
            return <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="flex flex-col sm:flex-row gap-6 p-8 rounded-3xl bg-[#E8F0F8]/50 border border-primary-dark/5 hover:border-accent/20 transition-colors" data-id="element-2587">
                  <div className="flex-shrink-0" data-id="element-2588">
                    <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center text-accent shadow-sm" data-id="element-2589">
                      <Icon className="w-7 h-7" data-id="element-2590" />
                    </div>
                  </div>
                  <div data-id="element-2591">
                    <h2 className="text-xl font-serif text-primary-dark mb-3" data-id="element-2592">
                      {point.title}
                    </h2>
                    <p className="text-primary-dark/70 leading-relaxed" data-id="element-2593">
                      {point.description}
                    </p>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Client Quote Block */}
      <section className="py-16 lg:py-24 px-4 md:px-6" data-id="element-2594">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="w-[95%] mx-auto bg-primary-dark rounded-3xl overflow-hidden relative" data-id="element-2595">
          <div className="grid lg:grid-cols-2 min-h-[500px]" data-id="element-2596">
            {/* Left — Image */}
            <div className="relative h-72 lg:h-auto" data-id="element-2597">
              <img src="https://cdn.magicpatterns.com/uploads/25TP9PqASwBfhD3FYFT9Py/pasted-image.jpg" alt="Automotive industry client — AccuTemp commercial HVAC" className="absolute inset-0 w-full h-full object-cover" data-id="element-2598" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-primary-dark/30 hidden lg:block" data-id="element-2599"></div>
            </div>

            {/* Right — Quote */}
            <div className="relative flex flex-col justify-center p-10 md:p-14 lg:p-16" data-id="element-2600">
              {/* Large decorative quote mark */}
              <div className="text-[220px] md:text-[280px] leading-none font-serif text-accent/10 select-none absolute -top-16 -left-4 pointer-events-none" data-id="element-2601">
                "
              </div>

              <div className="relative z-10" data-id="element-2602">
                <span className="text-xs uppercase tracking-[0.2em] font-medium text-accent mb-8 block" data-id="element-2603">
                  Client testimonial
                </span>

                <blockquote className="text-2xl md:text-3xl lg:text-4xl font-serif text-white leading-[1.5] mb-10 tracking-tight" data-id="element-2604">
                  "In our industry you feel every one percent. Getting air
                  quality and temperature right in our offices has been a
                  game-changer for focus and performance."
                </blockquote>

                <div className="flex items-center gap-5" data-id="element-2605">
                  <div className="w-12 h-[2px] bg-accent" data-id="element-2606"></div>
                  <div data-id="element-2607">
                    <div className="text-white font-medium mb-0.5" data-id="element-2608">CEO</div>
                    <div className="text-white/50 text-sm" data-id="element-2609">
                      Automotive Industry
                    </div>
                  </div>
                </div>
              </div>

              {/* Top-right link */}
              <Link to="/case-studies" className="absolute top-8 right-8 md:top-10 md:right-10 hidden md:flex items-center gap-2 text-white/50 hover:text-white text-sm transition-colors" data-id="element-2610">
                See more stories
                <ArrowRight className="w-4 h-4" data-id="element-2611" />
              </Link>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Daikin Partnership */}
      <section className="bg-white py-24" data-id="element-2612">
        <div className="px-10 md:px-16 lg:px-24" data-id="element-2613">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-2614">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-2615">
              <div className="flex items-center gap-6 mb-8" data-id="element-2616">
                <img src="https://cdn.magicpatterns.com/uploads/khPQKG24aZn2r3EidJR6xg/client-daikin.svg" alt="Daikin" className="h-8 object-contain" data-id="element-2617" />
                <div className="w-px h-10 bg-primary-dark/10" data-id="element-2618"></div>
                <img src="https://cdn.magicpatterns.com/uploads/2vVokvhsbE3igxg2bfPash/d1.png" alt="D1 Premium Partner" className="h-12 object-contain" data-id="element-2619" />
              </div>
              <h2 className="text-3xl md:text-4xl font-serif text-primary-dark mb-6" data-id="element-2620">
                Daikin D1+ Premier Partners
              </h2>
              <p className="text-primary-dark/70 text-lg leading-relaxed mb-8" data-id="element-2621">
                As a D1+ Premier Partner, we offer the highest level of Daikin
                expertise in the UK. This means extended warranties of up to 10
                years, priority technical support, and engineers trained
                directly by the manufacturer.
              </p>
              <Link to="/#contact" className="group inline-flex items-center gap-3 bg-primary-dark hover:bg-primary-dark/90 text-white px-8 py-4 rounded-full font-medium transition-all duration-300" data-id="element-2622">
                See the difference for yourself
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-2623" />
              </Link>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="relative rounded-2xl overflow-hidden aspect-square md:aspect-[4/3]" data-id="element-2624">
              <img src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80" alt="Commercial HVAC system in office building" className="w-full h-full object-cover" data-id="element-2625" />
              <div className="absolute inset-0 bg-primary-dark/10 mix-blend-multiply" data-id="element-2626"></div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>;
}